import React, { useEffect, useMemo, useState } from 'react';
import { auditFetch } from '../utils/auditFetch';
import { getAppointmentStatusLabel } from '../clinical/appointmentStatus';
import { getTriagePriorityLabel } from '../clinical/preTriage';
import ClinicalFilesPanel from './ClinicalFilesPanel';
import PreTriagePanel from './PreTriagePanel';

const initialChecks = {
  patientDataVerified: false,
  documentsVerified: false,
  consentVerified: false,
  consultationReasonVerified: false,
};

const VirtualAdmissionPanel = ({ user }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState('');
  const [admissionData, setAdmissionData] = useState(null);
  const [form, setForm] = useState({
    checks: initialChecks,
    consultationReason: '',
    notes: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedAppointment = useMemo(
    () => appointments.find((appointment) => appointment.id === selectedAppointmentId) || null,
    [appointments, selectedAppointmentId]
  );

  const loadAppointments = () => {
    auditFetch(`${backendUrl}/api/agenda/appointments`, {}, user)
      .then((res) => res.json())
      .then((data) => {
        const nextAppointments = data.appointments || [];
        setAppointments(nextAppointments);
        setSelectedAppointmentId((current) => current || nextAppointments[0]?.id || '');
      })
      .catch((error) => {
        console.error('Error loading admission appointments:', error);
        setMessage('No se pudo cargar la lista de citas.');
      });
  };

  const loadAdmission = (appointmentId) => {
    if (!appointmentId) return;
    setLoading(true);
    auditFetch(`${backendUrl}/api/admissions/virtual/${appointmentId}`, {}, user)
      .then((res) => res.json())
      .then((data) => {
        if (!data.success) {
          setMessage(data.message || 'No se pudo cargar admision.');
          return;
        }
        const savedAdmission = data.admission || {};
        setAdmissionData(data);
        setForm({
          checks: {
            ...initialChecks,
            ...(savedAdmission.checks || {}),
          },
          consultationReason: savedAdmission.consultationReason || data.appointment?.reason || '',
          notes: savedAdmission.notes || '',
        });
      })
      .catch((error) => {
        console.error('Error loading virtual admission:', error);
        setMessage('No se pudo cargar la admision virtual.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAdmission(selectedAppointmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppointmentId]);

  const updateCheck = (field, value) => {
    setForm((current) => ({
      ...current,
      checks: {
        ...current.checks,
        [field]: value,
      },
    }));
  };

  const saveAdmission = async (readyForDoctor = false) => {
    if (!selectedAppointmentId) return;
    const response = await auditFetch(`${backendUrl}/api/admissions/virtual/${selectedAppointmentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        readyForDoctor,
      }),
    }, user);
    const data = await response.json();
    setMessage(response.ok
      ? (readyForDoctor ? 'Paciente listo para atencion medica.' : 'Revision de admision guardada.')
      : data.message);
    if (response.ok) {
      loadAppointments();
      loadAdmission(selectedAppointmentId);
    }
  };

  const consentAccepted = admissionData?.derivedChecks?.hasConsentAccepted;
  const fileCount = admissionData?.derivedChecks?.clinicalFileCount || 0;
  const profile = admissionData?.profile;
  const admission = admissionData?.admission;

  return (
    <div className="virtual-admission-panel">
      <div className="section-header">
        <div>
          <h2>Admision virtual</h2>
          <p>Verificacion previa de paciente, documentos, consentimiento y motivo de consulta.</p>
        </div>
        <button className="btn btn-secondary" onClick={loadAppointments}>Actualizar</button>
      </div>

      <div className="admission-layout">
        <div className="admission-list">
          {appointments.length === 0 ? (
            <div className="empty-state"><p>No hay citas para admision.</p></div>
          ) : appointments.map((appointment) => (
            <button
              key={appointment.id}
              className={`admission-appointment ${appointment.id === selectedAppointmentId ? 'selected' : ''}`}
              onClick={() => setSelectedAppointmentId(appointment.id)}
            >
              <strong>{appointment.patient?.name || 'Paciente'}</strong>
              <span>{new Date(appointment.datetime).toLocaleString('es-EC')}</span>
              <small>{getAppointmentStatusLabel(appointment.status)} · {appointment.admission?.readyForDoctor ? 'Lista para medico' : 'Pendiente admision'}</small>
              <small>Pretriaje: {appointment.preTriage ? getTriagePriorityLabel(appointment.preTriage.priority) : 'Pendiente'}</small>
            </button>
          ))}
        </div>

        <div className="admission-workspace">
          {!selectedAppointment ? (
            <div className="empty-state"><p>Selecciona una cita para iniciar la revision.</p></div>
          ) : (
            <>
              <div className="admission-summary">
                <div>
                  <span>Paciente</span>
                  <strong>{selectedAppointment.patient?.name || 'No registrado'}</strong>
                  <small>{profile?.documentId || selectedAppointment.patient?.documentId || 'Documento pendiente'}</small>
                </div>
                <div>
                  <span>Medico</span>
                  <strong>{selectedAppointment.doctor?.name || 'No asignado'}</strong>
                  <small>{selectedAppointment.specialty || selectedAppointment.doctor?.specialty || 'Especialidad pendiente'}</small>
                </div>
                <div>
                  <span>Estado admision</span>
                  <strong>{admission?.readyForDoctor ? 'Lista para medico' : 'En revision'}</strong>
                  <small>{admission?.updatedAt ? new Date(admission.updatedAt).toLocaleString('es-EC') : 'Sin registro previo'}</small>
                </div>
                <div>
                  <span>Pretriaje</span>
                  <strong>{selectedAppointment.preTriage ? getTriagePriorityLabel(selectedAppointment.preTriage.priority) : 'Pendiente'}</strong>
                  <small>{selectedAppointment.preTriage?.updatedAt ? new Date(selectedAppointment.preTriage.updatedAt).toLocaleString('es-EC') : 'Sin clasificacion'}</small>
                </div>
              </div>

              {loading ? (
                <p className="save-message">Cargando admision...</p>
              ) : (
                <>
                  <div className="admission-check-grid">
                    <label className="admission-check">
                      <input
                        type="checkbox"
                        checked={form.checks.patientDataVerified}
                        onChange={(event) => updateCheck('patientDataVerified', event.target.checked)}
                      />
                      <span>
                        <strong>Datos del paciente verificados</strong>
                        <small>{profile ? 'Ficha encontrada en el sistema.' : 'No hay ficha completa guardada.'}</small>
                      </span>
                    </label>

                    <label className="admission-check">
                      <input
                        type="checkbox"
                        checked={form.checks.documentsVerified}
                        onChange={(event) => updateCheck('documentsVerified', event.target.checked)}
                      />
                      <span>
                        <strong>Documentos revisados</strong>
                        <small>{fileCount} archivo(s) clinico(s) asociado(s) a la cita.</small>
                      </span>
                    </label>

                    <label className="admission-check">
                      <input
                        type="checkbox"
                        checked={form.checks.consentVerified}
                        onChange={(event) => updateCheck('consentVerified', event.target.checked)}
                      />
                      <span>
                        <strong>Consentimiento confirmado</strong>
                        <small>{consentAccepted ? 'Consentimiento aceptado por el paciente.' : 'Consentimiento pendiente o rechazado.'}</small>
                      </span>
                    </label>

                    <label className="admission-check">
                      <input
                        type="checkbox"
                        checked={form.checks.consultationReasonVerified}
                        onChange={(event) => updateCheck('consultationReasonVerified', event.target.checked)}
                      />
                      <span>
                        <strong>Motivo de consulta validado</strong>
                        <small>Queda visible para el medico antes de iniciar.</small>
                      </span>
                    </label>
                  </div>

                  <PreTriagePanel
                    appointment={selectedAppointment}
                    user={user}
                    compact
                  />

                  <label className="admission-field">
                    <span>Motivo de consulta</span>
                    <textarea
                      value={form.consultationReason}
                      onChange={(event) => setForm({ ...form, consultationReason: event.target.value })}
                      placeholder="Describe el motivo principal referido por el paciente"
                    />
                  </label>

                  <ClinicalFilesPanel
                    appointmentId={selectedAppointment.id}
                    patientId={selectedAppointment.patient?.id || selectedAppointment.patientId}
                    patientName={selectedAppointment.patient?.name}
                    user={user}
                    canUpload={false}
                    compact
                  />

                  <label className="admission-field">
                    <span>Notas administrativas para el medico</span>
                    <textarea
                      value={form.notes}
                      onChange={(event) => setForm({ ...form, notes: event.target.value })}
                      placeholder="Documento pendiente, aclaracion de contacto, observaciones de admision"
                    />
                  </label>

                  {message && <p className="save-message">{message}</p>}

                  <div className="admission-actions">
                    <button className="btn btn-secondary" onClick={() => saveAdmission(false)}>Guardar revision</button>
                    <button className="btn btn-success" onClick={() => saveAdmission(true)}>Dejar listo para medico</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualAdmissionPanel;
